import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaUsers, FaCertificate, FaBook, FaTrophy } from 'react-icons/fa';

const countUp = keyframes`
  from {
    transform: translateY(15px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const Section = styled.section`
  padding: 3rem 5%;
  margin: 3rem 0;
  background: #ffffff;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e0d8f0, transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e0d8f0, transparent);
  }

  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 2rem 4%;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem 1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  border: 1px solid #f0f0f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  animation: ${countUp} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.accentColor || '#3730A3'};
    border-radius: 4px 4px 0 0;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(55, 48, 163, 0.1);

    .stat-icon {
      animation: ${float} 1.5s ease-in-out infinite;
      transform: scale(1.1);
    }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.3rem 1rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const IconWrapper = styled.div`
  width: 65px;
  height: 65px;
  border-radius: 18px;
  background: ${props => props.iconBg || '#f5f3ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.2rem;
  transition: all 0.4s ease;
  
  svg {
    font-size: 1.8rem;
    color: ${props => props.iconColor || '#3730A3'};
    transition: all 0.4s ease;
  }

  ${StatCard}:hover & {
    background: ${props => props.hoverIconBg || '#3730A3'};
    
    svg {
      color: white;
    }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    width: 50px;
    height: 50px;
    margin-bottom: 0.8rem;
    svg {
      font-size: 1.4rem;
    }
  }
`;

const StatNumber = styled.div`
  font-size: 2.8rem;
  font-weight: 900;
  color: #1a1a2e;
  line-height: 1;
  margin-bottom: 0.3rem;
  letter-spacing: -1px;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.7rem;
  }
`;

const StatSubtext = styled.div`
  font-size: 0.75rem;
  color: ${props => props.color || '#888'};
  margin-top: 0.4rem;
  font-weight: 500;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.65rem;
  }
`;

const StatsSection = () => {
  const [counts, setCounts] = useState({
    students: 0,
    passRate: 0,
    courses: 0,
    experience: 0
  });

  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          const targets = {
            students: 5000,
            passRate: 98,
            courses: 30,
            experience: 17

          };

          const duration = 1500;
          const steps = 45;
          let currentStep = 0;

          const interval = setInterval(() => {
            if (currentStep < steps) {
              setCounts({
                students: Math.min(Math.round((targets.students / steps) * currentStep), targets.students),
                passRate: Math.min(Math.round((targets.passRate / steps) * currentStep), targets.passRate),
                courses: Math.min(Math.round((targets.courses / steps) * currentStep), targets.courses),
                experience: Math.min(Math.round((targets.experience / steps) * currentStep), targets.experience)
              });
              currentStep++;
            } else {
              setCounts({
                students: targets.students,
                passRate: targets.passRate,
                courses: targets.courses,
                experience: targets.experience
              });
              clearInterval(interval);
            }
          }, duration / steps);

          return () => clearInterval(interval);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  const stats = [
    {
      icon: <FaUsers />,
      number: counts.students.toLocaleString() + '+',
      label: 'Students Trained',
      subtext: 'Globally',
      iconBg: '#e8f5e9',
      iconColor: '#2e7d32',
      hoverIconBg: '#2e7d32',
      accentColor: '#2e7d32'
    },
    {
      icon: <FaCertificate />,
      number: counts.passRate + '%',
      label: 'Pass Rate',
      subtext: 'Certification',
      iconBg: '#e3f2fd',
      iconColor: '#1565c0',
      hoverIconBg: '#1565c0',
      accentColor: '#1565c0'
    },
    {
      icon: <FaBook />,
      number: counts.courses + '+',
      label: 'Expert Courses',
      subtext: '& Growing',
      iconBg: '#fff3e0',
      iconColor: '#e65100',
      hoverIconBg: '#e65100',
      accentColor: '#e65100'
    },
    {
      icon: <FaTrophy />,
      number: counts.experience + '+',
      label: 'Years Excellence',
      subtext: 'Since 2008',
      iconBg: '#f3e5f5',
      iconColor: '#6a1b9a',
      hoverIconBg: '#6a1b9a',
      accentColor: '#6a1b9a'
    }
  ];

  return (
    <Section ref={sectionRef}>
      <Container>
        {stats.map((stat, index) => (
          <StatCard 
            key={index} 
            delay={`${index * 0.1}s`}
            accentColor={stat.accentColor}
          >
            <IconWrapper 
              className="stat-icon"
              iconBg={stat.iconBg}
              iconColor={stat.iconColor}
              hoverIconBg={stat.hoverIconBg}
            >
              {stat.icon}
            </IconWrapper>
            <StatNumber>{stat.number}</StatNumber>
            <StatLabel>{stat.label}</StatLabel>
            <StatSubtext color={stat.iconColor}>{stat.subtext}</StatSubtext>
          </StatCard>
        ))}
      </Container>
    </Section>
  );
};

export default StatsSection;